import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

const uploadSeo = (
  <Seo
    title="Upload Your Swing"
    description="Members upload their golf swing here for a frame-by-frame coach teardown from Dr. Cuts. Up to 3 swing uploads each month with a Cut Hunter membership."
    path="/upload"
    noindex
  />
);

export default function UploadPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user && (user.is_pro || user.role === "admin")) {
      api.get("/videos/quota").then((r) => setQuota(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen pt-32 px-6 text-center" data-testid="upload-need-login">
        {uploadSeo}
        <h1 className="font-display text-5xl">Log in first.</h1>
        <Link to="/login" className="btn-primary mt-8 inline-block">Log in</Link>
      </div>
    );
  }

  if (!user.is_pro && user.role !== "admin") {
    return (
      <div className="min-h-screen pt-32 px-6" data-testid="upload-gate">
        {uploadSeo}
        <div className="max-w-xl mx-auto text-center">
          <div className="overline">Members only</div>
          <h1 className="font-display text-5xl mt-4">Upload is gated.</h1>
          <p className="text-white/65 mt-4">The upload bay is reserved for Cut Hunter members. $10/month — cancel anytime.</p>
          <Link to="/pricing" className="btn-primary mt-8 inline-block" data-testid="upload-gate-cta">Become a Member</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Please choose a video file."); return; }
    setErr(""); setBusy(true); setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", desc);
    try {
      await api.post("/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      setSubmitted(true);
      api.get("/videos/quota").then((r) => setQuota(r.data)).catch(() => {});
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadAnother = () => {
    setFile(null); setTitle(""); setDesc(""); setProgress(0); setErr(""); setSubmitted(false);
  };

  return (
    <div data-testid="upload-page" className="min-h-screen pt-32 pb-24 px-6">
      {uploadSeo}
      <div className="max-w-2xl mx-auto">
        <div className="overline">Upload bay</div>
        <h1 className="font-display text-5xl mt-3">Drop your swing.</h1>
        <p className="text-white/60 mt-2">Private to you and your coach. Down-the-line or face-on. MP4 or MOV. Up to 500MB.</p>

        {submitted && (
          <div data-testid="upload-success" className="mt-10 border border-[#C9A227]/40 bg-[#C9A227]/[0.07] p-8">
            <div className="overline" style={{ color: "#C9A227" }}>Swing received</div>
            <h2 className="font-display text-3xl mt-3">Thanks — your swing is in the queue.</h2>
            <p className="text-white/70 mt-3">Our coach is reviewing submissions and will respond with feedback as soon as possible. You'll get an email the moment your teardown is ready.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={() => nav("/my-swings")} data-testid="upload-view-swings" className="btn-primary">View My Swings</button>
              <button onClick={uploadAnother} data-testid="upload-another"
                      disabled={quota && !quota.unlimited && quota.remaining <= 0}
                      className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed">Upload Another</button>
            </div>
            {quota && !quota.unlimited && (
              <p className="text-white/50 text-sm mt-4" data-testid="upload-success-quota">
                {quota.remaining > 0
                  ? `${quota.remaining} of ${quota.limit} uploads left this month.`
                  : `You've used all ${quota.limit} uploads this month. Your allowance resets on the 1st.`}
              </p>
            )}
          </div>
        )}

        {!submitted && (<>
        {quota && !quota.unlimited && (
          <div data-testid="upload-quota" className={`mt-6 border px-4 py-3 text-sm ${quota.remaining <= 0 ? "border-red-500/40 bg-red-500/[0.06] text-red-300" : "border-white/10 bg-white/[0.03] text-white/70"}`}>
            {quota.remaining > 0 ? (
              <span><b className="text-[#C9A227]">{quota.remaining}</b> of {quota.limit} swing uploads left this month.</span>
            ) : (
              <span>You've used all <b>{quota.limit}</b> uploads this month. Your allowance resets on the 1st.</span>
            )}
          </div>
        )}
        {quota && quota.unlimited && (
          <div data-testid="upload-quota" className="mt-6 border border-white/10 bg-white/[0.03] text-white/70 px-4 py-3 text-sm">
            Coach account — unlimited uploads.
          </div>
        )}

        <form onSubmit={submit} className="mt-10 space-y-6">
          <div>
            <label className="overline block mb-3" style={{color:"#A0B0A8"}}>Video file</label>
            <label htmlFor="vidfile" data-testid="upload-dropzone"
                   className="block border-2 border-dashed border-white/15 hover:border-[#C9A227] transition cursor-pointer p-10 text-center bg-white/[0.02]">
              {file ? (
                <div>
                  <div className="font-display text-2xl">{file.name}</div>
                  <div className="text-white/50 text-sm mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                </div>
              ) : (
                <div>
                  <div className="font-display text-2xl text-white/80">Click to select a swing video</div>
                  <div className="text-white/50 text-sm mt-2">or drag and drop</div>
                </div>
              )}
              <input id="vidfile" data-testid="upload-file-input" type="file" accept="video/*"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
          </div>

          <div>
            <label className="overline block mb-2" style={{color:"#A0B0A8"}}>Title</label>
            <input data-testid="upload-title" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)}
                   className="input-dark" placeholder="Driver — chasing a 7-yard cut" />
          </div>
          <div>
            <label className="overline block mb-2" style={{color:"#A0B0A8"}}>Notes for coach (optional)</label>
            <textarea data-testid="upload-desc" rows={4} maxLength={1000} value={desc} onChange={(e) => setDesc(e.target.value)}
                      className="input-dark" placeholder="Felt like I came over the top. Ball flew dead-straight, no shape." />
          </div>

          {busy && progress > 0 && (
            <div data-testid="upload-progress" className="space-y-2">
              <div className="overline">Uploading · {progress}%</div>
              <div className="h-1 bg-white/10">
                <div className="h-full bg-[#C9A227] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {err && <div className="text-red-400 text-sm" data-testid="upload-error">{err}</div>}

          <button data-testid="upload-submit" disabled={busy || (quota && !quota.unlimited && quota.remaining <= 0)} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? "Uploading..." : (quota && !quota.unlimited && quota.remaining <= 0) ? "Monthly limit reached" : "Submit Swing"}
          </button>
        </form>
        </>)}
      </div>
    </div>
  );
}
